/**
 * The mail accounts campaigns go out through.
 *
 * A workspace keeps a list rather than one connection, because providers
 * throttle, suspend and expire app passwords without warning, and a campaign
 * that dies at message forty because one mailbox hit its daily cap is exactly
 * the failure the list exists to survive. The send walks the switched-on
 * accounts from the top and carries on down rather than stopping — so the
 * order on this screen is the order it will try, and the switch is what
 * decides whether an account is in the running at all.
 *
 * The list stays a list: adding, editing and testing all happen in a dialog,
 * so the page never turns into a form with the accounts pushed off the top.
 *
 * Passwords are write-only by design: they are sent when set and never
 * returned, so a card shows whether one is stored rather than what it is.
 * Leaving the field blank on save keeps the stored one.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { Spinner, Switch, Pill } from '../../components/Primitives.jsx';
import Sheet, { Group, Field, OptionSelect } from '../../components/Sheet.jsx';
import { isOffline } from '../../lib/storage.js';
import api from '../../lib/api.js';

const BLANK = {
  name: '', host: '', port: 587, secure: false, user: '', pass: '',
  fromName: '', fromEmail: '', replyTo: '', enabled: true,
  watch: false, imapHost: '', imapPort: 993, imapUser: '', imapPass: ''
};

/**
 * Providers worth one click, since the ports are the usual thing to get wrong.
 *
 * A relay whose username is fixed carries it too: ZeptoMail authenticates as
 * the literal string `emailapikey` with the send-mail token as the password,
 * which is the single most common way that setup is filled in wrongly.
 */
const PRESETS = [
  ['Zoho (paid)', { host: 'smtppro.zoho.com', port: 465, secure: true }],
  ['Zoho (free)', { host: 'smtp.zoho.com', port: 465, secure: true }],
  ['ZeptoMail', { host: 'smtp.zeptomail.com', port: 465, secure: true, user: 'emailapikey' }],
  ['Gmail', { host: 'smtp.gmail.com', port: 587, secure: false }],
  ['Outlook', { host: 'smtp.office365.com', port: 587, secure: false }],
  ['Brevo', { host: 'smtp-relay.brevo.com', port: 587, secure: false }]
];

/**
 * Where a provider needs a word of warning that the fields cannot carry.
 *
 * Zoho is two products behind one name: a free account is on smtp.zoho.com, a
 * paid mailbox on smtppro.zoho.com, and each refuses the other's host. The
 * account's own settings page names which one it is — guessing costs an
 * afternoon.
 */
const PRESET_NOTE = {
  'smtppro.zoho.com':
    'Paid Zoho mailboxes use the pro servers. Check Zoho → Settings → Mail Accounts → your '
    + 'address: if it lists smtppro.zoho.com you are in the right place, and its IMAP server '
    + 'will be imappro.zoho.com.',
  'smtp.zoho.com':
    'Free Zoho accounts use these. A paid mailbox uses smtppro.zoho.com instead and will '
    + 'refuse this one — the account settings page in Zoho says which you have.',
  'smtp.zeptomail.com':
    'ZeptoMail logs in as emailapikey — the password is the send-mail token from its console, '
    + 'not your account password. Change the server to smtp.zeptomail.eu or .in if your '
    + 'ZeptoMail account is in that region.',
  'smtp.gmail.com':
    'Gmail needs an app password with 2-step verification on; your normal password is refused.',
  'smtp-relay.brevo.com':
    'Brevo logs in with your login email and an SMTP key, not your account password.'
};

/** What the server will read, worked out from where the account sends. */
const imapGuess = host => {
  const out = String(host || '').trim().toLowerCase();
  if (!out) return 'imap.example.com';
  if (out === 'smtppro.zoho.com') return 'imappro.zoho.com';
  if (out === 'smtp.zoho.com') return 'imap.zoho.com';
  if (out === 'smtp.office365.com') return 'outlook.office365.com';
  return out.startsWith('smtp') ? `imap${out.slice(4)}` : out;
};

const when = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(+d) ? '' : d.toLocaleString();
};

/* --------------------------------------------------------- the edit dialog */

function AccountDialog({ account, count, onSave, onClose, saving }) {
  const isNew = !account;
  const [form, setForm] = useState(() => ({ ...BLANK, ...(account || {}), pass: '' }));
  const [place, setPlace] = useState(() => String((account ? account.index : count) + 1));
  const { toast } = useApp();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* Port 465 is implicit SSL; 587 is STARTTLS. Getting this pair wrong is the
     usual reason a correct password still fails, so it follows the port. */
  const setPort = value => {
    const port = Number(value) || 0;
    setForm(f => ({ ...f, port, secure: port === 465 }));
  };

  const save = () => {
    if (!String(form.host).trim()) { toast('Which mail server?'); return; }
    if (isNew && !String(form.pass).trim()) { toast('A new account needs its password'); return; }
    onSave(
      { ...form, name: String(form.name).trim() || String(form.host).trim() },
      Number(place) - 1
    );
  };

  /* Where it sits in the queue. This is the only place order is set, so the
     card can stay a card instead of growing a pair of nudge arrows nobody
     could read the meaning of. */
  const places = Array.from({ length: isNew ? count + 1 : count }, (_, i) => String(i + 1));

  return (
    <Sheet
      icon="mail"
      tone="g-blue"
      title={isNew ? 'New mail account' : 'Edit mail account'}
      onClose={onClose}
      onSave={saving ? undefined : save}
      saveLabel={saving ? 'Saving…' : 'Save account'}
    >
      {isNew ? (
        <Field label="Start from" wide>
          <div className="chips">
            {PRESETS.map(([label, cfg]) => (
              <button
                type="button"
                key={label}
                className={`chip${form.host === cfg.host ? ' on' : ''}`}
                onClick={() => setForm(f => ({ ...f, ...cfg, name: f.name || label }))}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>
      ) : null}

      {PRESET_NOTE[String(form.host).trim().toLowerCase()] ? (
        <Field label="" wide>
          <p className="msnote">
            <Icon name="alert" />
            <span>{PRESET_NOTE[String(form.host).trim().toLowerCase()]}</span>
          </p>
        </Field>
      ) : null}

      <Group>Connection</Group>

      <Field label="Name it" hint={<div className="fhint">Only you see this</div>}>
        <input
          autoFocus
          value={form.name}
          placeholder="Zoho — billing"
          onChange={e => set('name', e.target.value)}
        />
      </Field>
      <Field label="Server">
        <input value={form.host} placeholder="smtp.zoho.com" onChange={e => set('host', e.target.value)} />
      </Field>
      <Field label="Port" hint={<div className="fhint">{form.secure ? 'SSL' : 'STARTTLS'}</div>}>
        <input type="number" value={form.port} onChange={e => setPort(e.target.value)} />
      </Field>
      <Field label="Username">
        <input
          value={form.user}
          placeholder="you@yourdomain.ae"
          onChange={e => set('user', e.target.value)}
        />
      </Field>
      <Field label="Password">
        <input
          type="password"
          value={form.pass}
          placeholder={isNew ? 'app password' : 'stored — type to replace'}
          onChange={e => set('pass', e.target.value)}
        />
      </Field>
      <Field
        label="Try it"
        hint={<div className="fhint">1 goes first; the rest are the fallback order</div>}
      >
        <OptionSelect value={place} onChange={setPlace} options={places} />
      </Field>

      <Group>How it arrives</Group>

      <Field label="" wide>
        <p className="muted" style={{ margin: 0 }}>
          The username above is what logs in; these are what the recipient sees, and they can
          be different addresses. Leave them blank to send as the username.
        </p>
      </Field>

      <Field label="From name">
        <input value={form.fromName} onChange={e => set('fromName', e.target.value)} />
      </Field>
      <Field label="From address">
        <input value={form.fromEmail} onChange={e => set('fromEmail', e.target.value)} />
      </Field>
      <Field label="Reply-to" wide>
        <input value={form.replyTo} onChange={e => set('replyTo', e.target.value)} />
      </Field>

      <Group>Replies</Group>

      <Field label="" wide>
        <label className="ruleopt">
          <input type="checkbox" checked={!!form.watch} onChange={e => set('watch', e.target.checked)} />
          <span>
            <b>Watch this mailbox for replies</b>
            <small>
              The server reads it every five minutes, so a campaign can show what came back and
              which addresses bounced. Reading only — it never sends from here.
            </small>
          </span>
        </label>
      </Field>

      {form.watch ? (
        <>
          <Field
            label="IMAP server"
            hint={<div className="fhint">Blank works it out from the server above</div>}
          >
            <input
              value={form.imapHost}
              placeholder={imapGuess(form.host)}
              onChange={e => set('imapHost', e.target.value)}
            />
          </Field>
          <Field label="IMAP port" hint={<div className="fhint">993 is SSL</div>}>
            <input
              type="number"
              value={form.imapPort || 993}
              onChange={e => set('imapPort', Number(e.target.value))}
            />
          </Field>
          <Field
            label="IMAP password"
            wide
            hint={
              <div className="fhint">
                Blank uses the sending password above, which is usually the same one.
              </div>
            }
          >
            <input
              type="password"
              value={form.imapPass}
              placeholder={isNew ? 'same as above' : 'stored — type to replace'}
              onChange={e => set('imapPass', e.target.value)}
            />
          </Field>
        </>
      ) : null}
    </Sheet>
  );
}

/* --------------------------------------------------------- the test dialog */

/**
 * One question — does this account work? — so it gets the small dialog and
 * keeps the answer where it was asked, rather than as a toast that is gone by
 * the time you have read it.
 */
function TestDialog({ account, onClose, onDone }) {
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const out = await api.mail.test({ id: account.id, to: to.trim() });
      setResult({
        ok: true,
        text: out.sent
          ? `Connected, and a test message is on its way to ${to.trim()}.`
          : 'Connected — the server accepted these settings.'
      });
      onDone();
    } catch (e) {
      setResult({ ok: false, text: e.message });
      onDone();
    }
    setBusy(false);
  };

  return (
    <Sheet
      compact
      icon="send"
      tone="g-green"
      title={`Test ${account.name}`}
      onClose={onClose}
      closeLabel="Close"
      onSave={busy ? undefined : run}
      saveLabel={busy ? 'Testing…' : 'Run the test'}
    >
      <Field label="" wide>
        <div className="mstestwho">
          <span className="segic"><Icon name="mail" /></span>
          <div>
            <b>{account.host}:{account.port}</b>
            <small>{account.user || 'no username'} · {account.secure ? 'SSL' : 'STARTTLS'}</small>
          </div>
        </div>
      </Field>

      <Field
        label="Send a real message to"
        wide
        hint={
          <div className="fhint">
            Leave it blank and this only opens the connection — nothing is delivered.
          </div>
        }
      >
        <input
          autoFocus
          value={to}
          placeholder="you@yourdomain.ae"
          onChange={e => setTo(e.target.value)}
        />
      </Field>

      {busy ? (
        <Field label="" wide>
          <p className="muted" style={{ margin: 0 }}><Spinner /> Opening the connection…</p>
        </Field>
      ) : null}

      {result ? (
        <Field label="" wide>
          <p className={`mstest ${result.ok ? 'ok' : 'bad'}`}>
            <Icon name={result.ok ? 'check' : 'alert'} />
            <span>{result.text}</span>
          </p>
        </Field>
      ) : null}
    </Sheet>
  );
}

/* ------------------------------------------------------------- the screen */

export default function MailSetup() {
  const { toast } = useApp();

  const [accounts, setAccounts] = useState([]);
  const [envFallback, setEnv] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState('');
  const [dialog, setDialog] = useState(null);        // { kind: 'edit' | 'test', id }
  const [busy, setBusy] = useState('');

  /*
   * A list that could not be fetched is not an empty list.
   *
   * Swallowing the error left the screen saying "no mail account" to a
   * workspace that had one — which is worse than saying nothing, because the
   * obvious next move is to add a duplicate. It says what went wrong instead,
   * and offers the retry.
   */
  const load = useCallback(() => {
    setLoading(true);
    setFailed('');
    api.mail.get()
      .then(out => { setAccounts(out.accounts || []); setEnv(!!out.envFallback); })
      .catch(e => setFailed(e.message || 'The server did not answer'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isOffline()) { setLoading(false); return; }
    load();
  }, [load]);

  if (isOffline()) {
    return <p className="muted">Sign in to set up a mail server — offline mode has no server to send from.</p>;
  }

  /** Every write goes through here, so the screen and the server never drift. */
  const persist = async (list, message) => {
    setBusy('save');
    try {
      const out = await api.mail.save({ accounts: list });
      setAccounts(out.accounts || []);
      setEnv(!!out.envFallback);
      if (message) toast(message);
      return true;
    } catch (e) {
      toast(e.message);
      return false;
    } finally {
      setBusy('');
    }
  };

  const editing = dialog && dialog.kind === 'edit'
    ? (dialog.id ? accounts.find(a => a.id === dialog.id) : null)
    : undefined;

  const testing = dialog && dialog.kind === 'test'
    ? accounts.find(a => a.id === dialog.id)
    : null;

  const saveAccount = async (record, index) => {
    const rest = record.id ? accounts.filter(a => a.id !== record.id) : [...accounts];
    const at = Math.max(0, Math.min(index, rest.length));
    rest.splice(at, 0, record);

    if (await persist(rest, record.id ? 'Mail account saved' : 'Mail account added')) setDialog(null);
  };

  const toggle = a =>
    persist(
      accounts.map(x => (x.id === a.id ? { ...x, enabled: !x.enabled } : x)),
      a.enabled ? `${a.name} is switched off` : `${a.name} is switched on`
    );

  const remove = a => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${a.name}? Campaigns will stop using it.`)) return;
    persist(accounts.filter(x => x.id !== a.id), 'Mail account removed');
  };

  const live = accounts.filter(a => a.enabled && a.hasPassword);

  return (
    <>
      {failed ? (
        <div className="msbar">
          <div className="msdot off"><Icon name="alert" /></div>
          <div>
            <b>Could not read your mail accounts</b>
            <small>{failed} — nothing has been changed.</small>
          </div>
          <button type="button" className="btn sm primary" onClick={load}>
            <Icon name="refresh" />Try again
          </button>
        </div>
      ) : (
        /* What will actually happen on the next send, said once at the top —
           the question this screen exists to answer. */
        <div className="msbar">
          <div className={`msdot ${live.length ? 'ok' : 'off'}`}>
            <Icon name={live.length ? 'check' : 'alert'} />
          </div>
          <div>
            <b>
              {live.length === 0
                ? (envFallback ? 'Falling back to the server’s own mail settings' : 'No mail account can send')
                : live.length === 1
                  ? `Sending through ${live[0].name}`
                  : `Sending through ${live[0].name}, then ${live.length - 1} more if it fails`}
            </b>
            <small>
              {live.length > 1
                ? 'A refused message moves down the list on its own — the campaign carries on.'
                : live.length === 1
                  ? 'Add a second account and a refused message will move to it instead of stopping.'
                  : 'Campaigns can only be handed off one message at a time until an account is on.'}
            </small>
          </div>
          <button type="button" className="btn sm primary" onClick={() => setDialog({ kind: 'edit' })}>
            <Icon name="plus" />Add account
          </button>
        </div>
      )}

      {loading ? <p className="muted"><Spinner /> Reading your mail settings…</p> : null}

      {!loading && !failed && !accounts.length ? (
        <p className="muted">
          No mail account yet. Add one and campaigns can go out in bulk instead of one
          hand-off at a time.
        </p>
      ) : null}

      <div className="mslist">
        {accounts.map((a, i) => (
          <article className={`mscard${a.enabled ? '' : ' off'}`} key={a.id}>
            <div className="mshead">
              <span className="msorder" title="Order it is tried in">{i + 1}</span>

              <div className="msname">
                <b>{a.name}</b>
                <small>{a.user || a.host}</small>
              </div>

              <div className="mstags">
                {a.watch ? <Pill tone="p-purple" icon="chat">reading replies</Pill> : null}
                {a.enabled && a.hasPassword && i === 0 ? <Pill tone="p-green">first choice</Pill> : null}
                {a.enabled && a.hasPassword && i > 0 ? <Pill tone="p-blue">fallback {i}</Pill> : null}
                {!a.hasPassword ? <Pill tone="p-amber">needs a password</Pill> : null}
                {a.lastError ? <Pill tone="p-red" icon="alert">last attempt failed</Pill> : null}
              </div>

              <Switch on={a.enabled} onClick={() => toggle(a)} title={a.enabled ? 'Switch off' : 'Switch on'} />
            </div>

            <dl className="msfacts">
              <div><dt>Server</dt><dd>{a.host}:{a.port}</dd></div>
              <div><dt>Security</dt><dd>{a.secure ? 'SSL' : 'STARTTLS'}</dd></div>
              <div><dt>Sends as</dt><dd>{a.fromEmail || a.user || '—'}</dd></div>
              <div><dt>Last used</dt><dd>{when(a.lastUsed) || 'never'}</dd></div>
              {a.watch ? <div><dt>Reads from</dt><dd>{a.imapHost}:{a.imapPort}</dd></div> : null}
            </dl>

            {a.lastError ? (
              <p className="mserr">
                <Icon name="alert" />
                <span>{a.lastError}{when(a.lastErrorAt) ? ` · ${when(a.lastErrorAt)}` : ''}</span>
              </p>
            ) : null}

            <div className="msacts">
              <button
                type="button"
                className="btn sm"
                onClick={() => setDialog({ kind: 'edit', id: a.id })}
              >
                <Icon name="edit" />Edit
              </button>
              <button
                type="button"
                className="btn sm"
                onClick={() => setDialog({ kind: 'test', id: a.id })}
                disabled={!a.hasPassword}
              >
                <Icon name="send" />Test
              </button>
              <button type="button" className="btn sm danger" onClick={() => remove(a)}>Remove</button>
            </div>
          </article>
        ))}
      </div>

      {editing !== undefined ? (
        <AccountDialog
          key={dialog.id || 'new'}
          account={editing ? { ...editing, index: accounts.findIndex(a => a.id === editing.id) } : null}
          count={accounts.length}
          saving={busy === 'save'}
          onSave={saveAccount}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {testing ? (
        <TestDialog
          key={testing.id}
          account={testing}
          onDone={load}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
